import { CardItem, Tabs } from "@hellocarbo/lasagne";
import {
  Calculator,
  ChartColumnDecreasing,
  ClipboardCopy,
  Cross,
  GitFork,
  Import,
  Users,
} from "lucide-react";
import { use } from "react";
import { useTranslation } from "react-i18next";

import { NavigationMenuLinks } from "@/components/NavigationMenu";
import OptionItem from "@/components/Options/OptionItem";
import ProServicesContent from "@/components/Options/ProServicesContent";
import { UserContext } from "@/contexts";
import { CustomUIMatch } from "@/hooks/useBreadcrumbs";
import useOutboundQueryParams from "@/hooks/useOutboundQueryParams";

export const handle = {
  activeMenuLink: () => NavigationMenuLinks.options,
  breadcrumb: () => [{ href: "/options", label: "options:title" }],
} as CustomUIMatch["handle"];

enum OPTIONS_VIEWS {
  byOptions = "options",
  byProServices = "services",
}

const iconMap = {
  simulation: Calculator,
  user: Users,
  import_deposit: Import,
  multi_entity: GitFork,
  external_score: ClipboardCopy,
  score: Cross,
  trajectory: ChartColumnDecreasing,
};

const OPTIONS_WITHOUT_CTA = [] as string[];
const OPTIONS_TO_REDIRECT_TO_DEMO_BOOKING = [
  "simulation",
  "multi_entity",
  "trajectory",
];

function Options() {
  const { t } = useTranslation(["common", "options"]);
  const { user } = use(UserContext);
  const outboundQueryParams = useOutboundQueryParams("options");

  // Any new option added in the back needs to be added here too to appear in the Options catalogue
  // The order in this array determines the order in which they will appear
  let optionsToDisplay = [
    "user",
    "import_deposit",
    "simulation",
    "multi_entity",
    "external_score",
    "trajectory",
    "score",
  ];

  if (user?.policies?.simulation.queries["create"]) {
    // Do not display the simulation option if the user already has it
    optionsToDisplay = optionsToDisplay.filter(
      (option) => option !== "simulation"
    );
  }
  if (user?.company.subscriptionType !== "essential") {
    // Premium have the option included, Pro and Start need to upgrade before asking for the option
    optionsToDisplay = optionsToDisplay.filter(
      (option) => option !== "trajectory"
    );
  }

  return (
    <CardItem>
      <Tabs
        defaultValue={OPTIONS_VIEWS.byOptions}
        tabs={[
          {
            value: OPTIONS_VIEWS.byOptions,
            label: t("options:byOptions.title"),
            content: (
              <ul className="gap-sm grid grid-cols-1 xl:grid-cols-2">
                {optionsToDisplay.map((optionKey: string) => {
                  const icon = iconMap[optionKey as keyof typeof iconMap];
                  const shouldNotDisplayCTA =
                    OPTIONS_WITHOUT_CTA.includes(optionKey);

                  const redirectToDemoBooking =
                    OPTIONS_TO_REDIRECT_TO_DEMO_BOOKING.includes(optionKey);

                  return (
                    <li key={optionKey}>
                      <OptionItem
                        optionKey={optionKey}
                        title={t(`options:optionsList.${optionKey}.title`)}
                        description={t(
                          `options:optionsList.${optionKey}.description`
                        )}
                        href={
                          redirectToDemoBooking
                            ? `https://meetings-eu1.hubspot.com/emmanuelle-vignial/demo-options-carbo${outboundQueryParams}`
                            : "/options/subscription"
                        }
                        tag={t(`options:optionsList.${optionKey}.tag`)}
                        Icon={icon}
                        shouldNotDisplayCTA={shouldNotDisplayCTA}
                        tooltipContent={t(
                          redirectToDemoBooking
                            ? "common:bookADemo"
                            : "options:tooltip"
                        )}
                        shouldOpenNewTab={redirectToDemoBooking}
                      />
                    </li>
                  );
                })}
              </ul>
            ),
          },
          {
            value: OPTIONS_VIEWS.byProServices,
            label: t("options:byProServices.title"),
            content: <ProServicesContent />,
          },
        ]}
      />
    </CardItem>
  );
}

export default Options;
